import { coercePref } from './coerce-pref.util';

/** The user's stored choice. 'system' follows the OS via prefers-color-scheme; the other two
 *  pin a theme regardless of it. */
export type ThemePreset = 'system' | 'light' | 'dark';

/** What actually gets painted — the value written to the `data-theme` attribute and to the
 *  localStorage mirror. Deliberately a SEPARATE type from ThemePreset: the mirror stores the
 *  RESOLVED theme, so the inline boot script in index.html never has to re-implement preset
 *  resolution (which in SP2 means reading prefers-color-scheme before Angular exists). */
export type ResolvedTheme = 'dark' | 'light';

/** Key in the /v2/userSettings blob. Frontend-owned and opaque to the backend. */
export const THEME_KEY = 'display.theme';

/** localStorage key holding the RESOLVED theme, read by the inline script in index.html.
 *  Namespaced because localStorage is shared across the whole origin. */
export const THEME_STORAGE_KEY = 'g15.theme';

export const THEME_PRESETS: readonly ThemePreset[] = ['system', 'light', 'dark'] as const;

/** Coerce an untrusted value (a settings-blob entry or a cross-tab broadcast payload) to a
 *  known preset. Routes through coercePref so the prototype-key hardening is shared with every
 *  other display.* preference rather than re-hand-rolled here. */
export function coerceTheme(v: unknown): ThemePreset {
  return coercePref(v, THEME_PRESETS, 'system');
}

/**
 * Map a stored preset to the theme actually painted.
 *
 * A PURE FUNCTION OF ITS TWO ARGUMENTS, deliberately: `prefersLight` is passed in rather than read
 * from `matchMedia` here, so the mapping is unit-testable under both OS settings without a browser
 * and without stubbing a global. ThemeService owns the media query; this owns the rule.
 *
 * The default keeps every existing caller compiling and keeps the app's historical behaviour for
 * anyone who does not pass it: no preference expressed, dark theme.
 */
export function resolveTheme(preset: ThemePreset, prefersLight = false): ResolvedTheme {
  if (preset === 'light' || preset === 'dark') return preset;
  return prefersLight ? 'light' : 'dark';
}
